import type { SheetChange, SheetColumn, SheetOption, SheetParsed, SheetRowId } from "./types";

/**
 * Pure helpers behind DataSheet: reading, parsing and printing cell values,
 * the Excel clipboard format, and the page-side counts (unsaved cells,
 * rows blocked by an error).
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const columnValue = <R,>(column: SheetColumn<R>, row: R): unknown =>
    column.getValue ? column.getValue(row) : (row as unknown as Record<string, unknown>)[column.key];

export const columnOptions = <R,>(column: SheetColumn<R>, row?: R): SheetOption[] =>
    typeof column.options === "function" ? column.options(row) : column.options ?? [];

export const isBlank = (value: unknown): boolean =>
    value === null || value === undefined || (typeof value === "string" && value.trim() === "");

export const isReadOnly = <R,>(column: SheetColumn<R>, row: R | undefined): boolean => {
    if (typeof column.readOnly === "function") return row === undefined ? false : column.readOnly(row);
    return !!column.readOnly;
};

export const isNumericColumn = <R,>(column: SheetColumn<R>): boolean => column.type === "number" || column.type === "money";

/** "1,250.50", "AED 1250", "1250 AED", "$300", " 300 " → a number; anything else ("12abc") → null. */
export const toNumber = (value: unknown): number | null => {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value !== "string") return null;
    // A currency code or symbol may lead; a trailing code must be set off by a space so "12abc" stays invalid.
    const cleaned = value
        .trim()
        .replace(/^(?:[A-Za-z]{3}\s*|[$€£]\s*)/, "")
        .replace(/(?:\s+[A-Za-z]{3}|\s*[$€£])$/, "")
        .replace(/[,\s]/g, "");
    if (!/^-?\d+(\.\d+)?$/.test(cleaned) && !/^-?\.\d+$/.test(cleaned)) return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
};

const pad = (n: number) => String(n).padStart(2, "0");
const isoOf = (year: number, month: number, day: number): string | null => {
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
    return `${year}-${pad(month)}-${pad(day)}`;
};

/** "2026-10-12", "12/10/2026", "12 Oct 2026", "Oct 12, 2026", an Excel serial → "2026-10-12". */
export const parseDateText = (text: string): string | null => {
    const value = text.trim();
    let match = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(value);
    if (match) return isoOf(Number(match[1]), Number(match[2]), Number(match[3]));
    match = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/.exec(value);
    if (match) {
        const year = Number(match[3]) < 100 ? 2000 + Number(match[3]) : Number(match[3]);
        return isoOf(year, Number(match[2]), Number(match[1]));
    }
    const monthOf = (name: string) => MONTHS.findIndex((m) => name.toLowerCase().startsWith(m.toLowerCase())) + 1;
    match = /^(\d{1,2})\s+([A-Za-z]+)\.?,?\s+(\d{4})$/.exec(value);
    if (match && monthOf(match[2]) > 0) return isoOf(Number(match[3]), monthOf(match[2]), Number(match[1]));
    match = /^([A-Za-z]+)\.?\s+(\d{1,2}),?\s+(\d{4})$/.exec(value);
    if (match && monthOf(match[1]) > 0) return isoOf(Number(match[3]), monthOf(match[1]), Number(match[2]));
    if (/^\d{5}$/.test(value)) {
        // Excel stores dates as days since 30 Dec 1899.
        const date = new Date(Date.UTC(1899, 11, 30) + Number(value) * 86400000);
        return isoOf(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
    }
    return null;
};

/** "9:30", "09.30", "930", "9" → "09:30" / "09:00". */
export const parseTimeText = (text: string): string | null => {
    const match = /^(\d{1,2})(?:[:.h]?(\d{2}))?$/.exec(text.trim());
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = match[2] ? Number(match[2]) : 0;
    if (hours > 23 || minutes > 59) return null;
    return `${pad(hours)}:${pad(minutes)}`;
};

export const formatDateText = (value: unknown): string => {
    if (typeof value !== "string") return value === null || value === undefined ? "" : String(value);
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return value;
    return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
};

export const formatNumberText = (value: number, decimals?: number): string =>
    value.toLocaleString("en-US", decimals === undefined ? { maximumFractionDigits: 6 } : { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

/**
 * The cell as plain text — what Ctrl+C copies, Export writes, filters match
 * and sorting compares. Numbers stay plain ("1250.5") so Excel reads them
 * as numbers.
 */
export const cellText = <R,>(column: SheetColumn<R>, row: R, value: unknown = columnValue(column, row)): string => {
    if (column.toText) return column.toText(value, row);
    if (isBlank(value)) return "";
    switch (column.type) {
        case "select": {
            const option = columnOptions(column, row).find((o) => String(o.value) === String(value));
            return option ? option.label : String(value);
        }
        case "number":
        case "money": {
            const n = toNumber(value);
            return n === null ? String(value) : String(n);
        }
        case "date":
            return formatDateText(value);
        default:
            return String(value);
    }
};

/** Typed or pasted text → the column's value, or a message saying what fits. */
export const parseCellText = <R,>(column: SheetColumn<R>, text: string, row?: R): SheetParsed => {
    if (column.parse) return column.parse(text, row);
    const value = text.trim();
    switch (column.type) {
        case "number":
        case "money": {
            if (!value) return { value: null };
            const n = toNumber(value);
            return n === null ? { error: "Numbers only, like 1250." } : { value: n };
        }
        case "date": {
            if (!value) return { value: null };
            const iso = parseDateText(value);
            return iso ? { value: iso } : { error: "Write a date like 12 Oct 2026." };
        }
        case "time": {
            if (!value) return { value: null };
            const time = parseTimeText(value);
            return time ? { value: time } : { error: "Write a time like 09:30." };
        }
        case "select": {
            if (!value) return { value: null };
            const wanted = value.toLowerCase();
            const options = columnOptions(column, row);
            const option = options.find((o) => o.label.toLowerCase() === wanted || String(o.value).toLowerCase() === wanted)
                ?? options.find((o) => o.label.toLowerCase().startsWith(wanted));
            return option ? { value: option.value } : { error: `No “${value}” in the ${column.label} list.` };
        }
        default:
            return { value: text.replace(/\s+$/, "").replace(/^\s+/, "") };
    }
};

/** Equal for the sheet's purposes (null, undefined and "" are all empty). */
export const sameValue = (a: unknown, b: unknown): boolean => {
    if (isBlank(a) && isBlank(b)) return true;
    if (typeof a === "object" || typeof b === "object") return JSON.stringify(a) === JSON.stringify(b);
    return String(a) === String(b);
};

/** The empty value a cleared cell gets. */
export const blankValueFor = <R,>(column: SheetColumn<R>): unknown => (column.type === "text" || !column.type ? "" : null);

// ── Excel clipboard ──────────────────────────────────────────────────────

/**
 * Excel / Sheets copy cells as tab-separated lines; a cell holding a tab,
 * newline or quote comes wrapped in quotes with "" for a quote.
 */
export const parseClipboard = (text: string): string[][] => {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = "";
    let quoted = false;
    let i = 0;
    const src = text.replace(/\r\n?/g, "\n");
    while (i < src.length) {
        const ch = src[i];
        if (quoted) {
            if (ch === "\"" && src[i + 1] === "\"") { cell += "\""; i += 2; continue; }
            if (ch === "\"") { quoted = false; i += 1; continue; }
            cell += ch;
            i += 1;
            continue;
        }
        if (ch === "\"" && cell === "") { quoted = true; i += 1; continue; }
        if (ch === "\t") { row.push(cell); cell = ""; i += 1; continue; }
        if (ch === "\n") { row.push(cell); rows.push(row); row = []; cell = ""; i += 1; continue; }
        cell += ch;
        i += 1;
    }
    if (cell !== "" || row.length) {
        row.push(cell);
        rows.push(row);
    }
    // Excel ends a copy with a newline — no trailing empty line.
    while (rows.length && rows[rows.length - 1].every((c) => c === "")) rows.pop();
    return rows;
};

export const toClipboardText = (grid: string[][]): string =>
    grid.map((line) => line.map((cell) => (/[\t\n"]/.test(cell) ? `"${cell.replace(/"/g, "\"\"")}"` : cell)).join("\t")).join("\n");

const normaliseHeader = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Is the first pasted line a header row (the Excel column titles)? Returns
 * which sheet column each pasted column maps to, and the titles with no
 * column here.
 */
export const matchHeaderLine = <R,>(line: string[], columns: SheetColumn<R>[]): { isHeader: boolean; keys: (string | null)[]; unmatched: string[]; matched: number } => {
    const byName = new Map<string, string>();
    columns.forEach((column) => {
        byName.set(normaliseHeader(column.label), column.key);
        byName.set(normaliseHeader(column.key), column.key);
    });
    const used = new Set<string>();
    const keys = line.map((title) => {
        const key = byName.get(normaliseHeader(title));
        if (!key || used.has(key)) return null;
        used.add(key);
        return key;
    });
    const nonEmpty = line.filter((title) => title.trim() !== "").length;
    const matched = keys.filter(Boolean).length;
    const isHeader = matched >= 2 && matched * 2 >= nonEmpty;
    const unmatched = line.filter((title, index) => title.trim() !== "" && !keys[index]);
    return { isHeader, keys, unmatched, matched };
};

// ── Page-side helpers ────────────────────────────────────────────────────

export interface SheetRowProblem {
    /** The column, or null for a whole-row rule. */
    key: string | null;
    message: string;
}

/** Every rule a row breaks — a row with any can't be saved. */
export const sheetRowErrors = <R,>(
    rows: R[],
    columns: SheetColumn<R>[],
    getRowId: (row: R) => SheetRowId,
    rowError?: (row: R) => string | null | undefined,
): Map<SheetRowId, SheetRowProblem[]> => {
    const result = new Map<SheetRowId, SheetRowProblem[]>();
    rows.forEach((row) => {
        const problems: SheetRowProblem[] = [];
        columns.forEach((column) => {
            const message = column.validate?.(columnValue(column, row), row);
            if (message) problems.push({ key: column.key, message });
        });
        const rowMessage = rowError?.(row);
        if (rowMessage) problems.push({ key: null, message: rowMessage });
        if (problems.length) result.set(getRowId(row), problems);
    });
    return result;
};

/** "N unsaved cells" for the SaveBar. */
export const countChangedCells = <R,>(rows: R[], columns: SheetColumn<R>[], isCellChanged: (row: R, key: string) => boolean): number =>
    rows.reduce((total, row) => total + columns.filter((column) => isCellChanged(row, column.key)).length, 0);

/** Applies a batch of cell edits to plain row objects (row[key] = value). */
export const applySheetChanges = <R,>(rows: R[], changes: SheetChange[], getRowId: (row: R) => SheetRowId): R[] => {
    if (!changes.length) return rows;
    const byRow = new Map<SheetRowId, SheetChange[]>();
    changes.forEach((change) => byRow.set(change.rowId, [...(byRow.get(change.rowId) ?? []), change]));
    return rows.map((row) => {
        const rowChanges = byRow.get(getRowId(row));
        if (!rowChanges) return row;
        const next = { ...(row as unknown as Record<string, unknown>) };
        rowChanges.forEach((change) => { next[change.key] = change.value; });
        return next as unknown as R;
    });
};

/** "Export to Excel": the visible columns and rows, numbers as numbers. */
export const exportSheetToExcel = async <R,>(fileName: string, columns: SheetColumn<R>[], rows: R[]): Promise<void> => {
    const ExcelJS = await import("exceljs");
    const { saveAs } = await import("file-saver");
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet((fileName || "Sheet").slice(0, 31));
    sheet.columns = columns.map((column) => ({ header: column.label, key: column.key, width: Math.max(10, Math.round((column.width ?? 140) / 7)) }));
    sheet.getRow(1).font = { bold: true };
    sheet.views = [{ state: "frozen", ySplit: 1 }];
    rows.forEach((row) => {
        const record: Record<string, unknown> = {};
        columns.forEach((column) => {
            const value = columnValue(column, row);
            record[column.key] = isNumericColumn(column) && !column.toText ? toNumber(value) : cellText(column, row, value);
        });
        sheet.addRow(record);
    });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${fileName}.xlsx`);
};
